/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import IFormConfiguration from 'apmui/components/form/Model';
import IDialogConfiguration, { DialogType } from 'apmui/components/dialog/Model';
import ITemplateModel from 'apmui/master/Model';
import { IClientConfiguration, OS } from 'apmui/plugin/Model';
import PageType from 'apmui/page/PageType';

interface IEpsRuntimeState {
    /**
     * Status for progress indicator.
     * Setting status will also display progress indicator.
     */
    status: string;
    /**
     * Optional text message within the content block.
     */
    text: string;
    /**
     * Notification popup text
     */
    notification: string;
    /**
     * Show 'browser needs restart' message.
     */
    needRestart: boolean;
}

interface IEpsCheckFailedDetail {
    apmError: number;
    httpError: number;
    retriesLeft: number;
}

enum CLIENT_TYPE {
    NORMAL = 'normal',
    STANDALONE = 'standalone',
}

/**
 * Configuration data specific to EndpointSecurity agent
 */
interface IEndpointSecurityPageModel extends ITemplateModel {
    /* Page type tag */
    pageType: PageType.EPS;

    /* Endpoint Security config */
    [PageType.EPS]: {
        state: Partial<IEpsRuntimeState>;
        components: IClientConfiguration;
        form?: IFormConfiguration;
        /**
         * Client
         */
        client: {
            type: CLIENT_TYPE;
            os: OS;
            customProtocol: boolean;
            nativeResources: boolean;
            edgeClientConfig?: {
                port: string;
                state: string;
                browser: string;
                os: string;
            };
        };
        /**
         * Strings
         */
        strings: {
            agent_endpoint_check_av: string;
            agent_endpoint_check_pm: string;
            agent_endpoint_check_as: string;
            agent_endpoint_check_p2p: string;
            agent_endpoint_check_hd: string;
            agent_endpoint_check_ha: string;
            agent_endpoint_check_file: string;
            agent_endpoint_check_fw: string;
            agent_endpoint_check_machinecert: string;
            agent_endpoint_check_process: string;
            agent_endpoint_windows_check_registry: string;
            agent_endpoint_windows_info_os: string;
            agent_endpoint_machine_info: string;
            agent_endpoint_check_software: string;
            // PWS
            agent_endpoint_windows_protected_workspace: string;
            agent_endpoint_windows_protected_workspace_resuming_short: string;
            agent_endpoint_windows_protected_workspace_resuming_long: string;
            agent_endpoint_windows_protected_workspace_continue: string;
            agent_endpoint_windows_protected_workspace_close_browser: string;
            //
            awaiting_connection: string;
            obtaining_status: string;
            //
            check_client: string;
            installing_message: string;
            downloading_message: string;
            edgeBrowserInstructionsMessage: string;
            openNewBrowserMessage: string;
            continue: string;
            // continue_no_inspection_host: string;
            activexrequired: string;
            restart_client_installation: string;
            //
            virtualkeyboard_custom: string;
            virtualkeyboard_hide: string;
        };
        dialogs: {
            [DialogType.EPS_AUTO_START_NOT_SUPPORTED]: IDialogConfiguration;
            [DialogType.EPS_FAILED_TO_PARSE_STATUS_UPDATE]: IDialogConfiguration;
            [DialogType.EPS_FAILED_TO_RETRIEVE_STATUS_UPDATE]: IDialogConfiguration;
            [DialogType.EPS_INSPECTION_HOST_NOT_CONNECTED]: IDialogConfiguration;
            [DialogType.EPS_INSTALL]: IDialogConfiguration;
            [DialogType.EPS_POPUP_BLOCKED]: IDialogConfiguration;
        };
    };
}

export {
    IEpsCheckFailedDetail,
    IEpsRuntimeState,
    IEndpointSecurityPageModel,
    IEndpointSecurityPageModel as default,
    CLIENT_TYPE,
};
